import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { take } from 'lib/array/arrayUtils'
import TextButton from 'LuxKit/Button/TextButton'
import styled from 'styled-components'
import type { CityDestination, CountryDestination, DestinationNavItemType } from 'components/Common/TopDestinationsList/TopDestinationList'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from '../Spacing/VerticalSpacer'

const DestinationItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-row-gap: ${rem(30)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(40)};

  ${mediaQueryUp.tablet} {
    align-items: center;
    margin-top: ${rem(24)};
  }
`

interface Props {
  destinations: Array<CountryDestination | CityDestination>
  toShowByDefault: number
  destinationType: DestinationNavItemType
}

function TopDestinations({ destinations, toShowByDefault, destinationType }: Props) {
  const intl = useIntl()
  const haveMoreToShow: boolean = destinations.length > toShowByDefault
  const [isOpenAllDestinations, setOpenAllDestinations] = useState<boolean>(false)

  const hotelsResortsText = intl.formatMessage({
    id: 'QH29tqbEj2',
    defaultMessage: 'Hotels & Resorts',
    description: 'Link text for hotels and resorts in top destinations list',
  })

  const showLessText = intl.formatMessage({
    id: 'su+GWniCoN',
    defaultMessage: 'Show less',
    description: 'Button text to show fewer destinations',
  })

  const showMoreText = useMemo(
    () => {
      switch (destinationType) {
        case 'Countries':
          return intl.formatMessage(
            {
              id: '6eNCiUzJDw',
              defaultMessage: 'Show {count} countries',
              description: 'Button text to show more countries',
            },
            {
              count: destinations.length,
            },
          )
        case 'Cities':
          return intl.formatMessage(
            {
              id: 'W9bXSQBz5M',
              defaultMessage: 'Show {count} cities',
              description: 'Button text to show more cities',
            },
            {
              count: destinations.length,
            },
          )
        case 'Popular':
          return intl.formatMessage(
            {
              id: 'Tce3gJSCp2',
              defaultMessage: 'Show {count} popular',
              description: 'Button text to show more popular destinations',
            },
            {
              count: destinations.length,
            },
          )
      }
    },
    [intl, destinations.length, destinationType],
  )

  return (
    <>
      <DestinationItemsContainer>
        {take(destinations, isOpenAllDestinations ? destinations.length : toShowByDefault).map(destination =>
          <VerticalSpacer gap={8} key={destination.name}>
            <BodyText colour="neutral-one" variant="medium" weight="bold">{destination.name}</BodyText>
            <TextLink size="small" weight="regular" to={destination.link}>
              {hotelsResortsText}
            </TextLink>
          </VerticalSpacer>)}
      </DestinationItemsContainer>
      { haveMoreToShow && <ButtonWrapper>
        <TextButton
          onClick={() => setOpenAllDestinations(!isOpenAllDestinations)}
          kind="secondary"
          size="large"
        >
          {isOpenAllDestinations ? showLessText : showMoreText}
        </TextButton>
      </ButtonWrapper>}
    </>
  )
}

export default TopDestinations
